import React, {ReactNode, useContext, useState} from "react";
import {AspectRatio, Box, Button, Container, Divider, FormControl, FormLabel, Input, Sheet, Stack, Typography} from "@mui/joy";
import firebase from "firebase/compat/app";
import "firebase/compat/auth"
import {Apple, Email, Google, Key, Visibility, VisibilityOff} from "@mui/icons-material";
import {useLocation} from "react-router-dom";
import {FeedbackContext} from "../utils/FeedbackContext";
import {GlassCard} from "../components/GlassCard";
import {Layout} from "../components/Layout";
import Logo from "../assets/app_logo.png"
import {useTranslation} from "react-i18next";

// Codici di errore Firebase da ignorare silenziosamente — l'utente ha
// solo chiuso il popup, non è un vero errore da mostrare in uno snackbar.
const IGNORED_SOCIAL_ERROR_CODES = new Set([
    "auth/popup-closed-by-user",
    "auth/cancelled-popup-request",
]);

function AuthCard({children}: {children: ReactNode}) {
    return (
        <Layout>
            <Container maxWidth="xs" sx={{flex: 1, display: "flex", alignItems: "center", py: {xs: 4, sm: 6}, px: {xs: 2, sm: 3}}}>
                <GlassCard sx={{width: "100%", p: {xs: 3, sm: 4}}}>
                    <Stack direction="column" spacing={2.5} sx={{width: "100%"}}>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection:"column", justifyContent:"center", alignItems: 'center' }}>
                            <AspectRatio ratio={1} sx={{width: 72, borderRadius: 18}}>
                                <Sheet variant="soft" sx={{backgroundColor:"white"}}>
                                    <img src={Logo} alt="Logo" width={80} height={80}/>
                                </Sheet>
                            </AspectRatio>
                            <Typography level="h3">CineFX</Typography>
                        </Box>
                        {children}
                    </Stack>
                </GlassCard>
            </Container>
        </Layout>
    )
}

export function Login() {

    const { t } = useTranslation();
    const {loading, setError} = useContext(FeedbackContext)
    enum TABS {
        LOGIN,
        RESET_PSW,
        CONFIRM_RESET_PSW,
    }
    const location = useLocation()

    const [tab, setTab] = useState<TABS>(TABS.LOGIN)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [pswvisible, setPswvisible] = useState(false)
    const [socialLoading, setSocialLoading] = useState<"google" | "apple" | null>(null)

    switch (tab) {
        case TABS.LOGIN :
            return (
                <AuthCard>
                    <FormControl>
                        <FormLabel>
                            {t("email")}
                        </FormLabel>
                        <Input type="email"
                               placeholder={t("emailplaceholder")}
                               value={email}
                               onChange={(e) => setEmail(e.target.value)}
                               startDecorator={<Email />}
                               onKeyDown={(event) => {
                                   if (event.key === 'Enter') {
                                       SignIn(email, password, location.state).then(() => {})
                                   }
                               }}
                               autoFocus
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>
                            {t("password")}
                        </FormLabel>
                        <Input type={pswvisible ? "text" : "password"}
                               placeholder="********"
                               value={password}
                               onChange={(e) => setPassword(e.target.value)}
                               startDecorator={<Key />}
                               endDecorator={
                                   pswvisible ?
                                       <VisibilityOff onClick={() => setPswvisible(!pswvisible)} />
                                       :
                                       <Visibility onClick={() => setPswvisible(!pswvisible)} />
                               }
                               onKeyDown={(event) => {
                                   if (event.key === 'Enter') {
                                       SignIn(email, password, location.state).then(() => {})
                                   }
                               }}
                        />
                    </FormControl>
                    <Button variant="solid"
                            color="primary"
                            onClick={() => SignIn(email, password, location.state)}
                            disabled={!email || !password || loading}
                    >
                        {t("login")}
                    </Button>

                    <Divider>{t("orcontinuewith")}</Divider>

                    <Stack direction={{xs: "column", sm: "row"}} spacing={1.5}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            fullWidth
                            startDecorator={<Google />}
                            loading={socialLoading === "google"}
                            disabled={socialLoading !== null}
                            onClick={() => SignInWithSocial("google", location.state)}
                        >
                            Google
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            fullWidth
                            startDecorator={<Apple />}
                            loading={socialLoading === "apple"}
                            disabled={socialLoading !== null}
                            onClick={() => SignInWithSocial("apple", location.state)}
                        >
                            Apple
                        </Button>
                    </Stack>

                    <Typography level="body-sm" textAlign="center" className="link" onClick={() => setTab(TABS.RESET_PSW)}>
                        {t("passwordforgot")}
                    </Typography>
                </AuthCard>
            )
        case TABS.RESET_PSW :
            return (
                <AuthCard>
                    <Typography level="body-sm" className="link" onClick={() => setTab(TABS.LOGIN)}>
                        &larr; {t("back")}
                    </Typography>

                    <FormControl>
                        <FormLabel>
                            Email
                        </FormLabel>
                        <Input type="email"
                               placeholder={t("emailplaceholder")}
                               value={email}
                               onChange={(e) => setEmail(e.target.value)}
                               startDecorator={<Email />}
                        />
                    </FormControl>

                    <Button variant="solid"
                            color="primary"
                            onClick={() => ResetPsw(email)}
                            disabled={!email || loading}
                    >
                        {t("resetpsw")}
                    </Button>
                </AuthCard>
            )
        case TABS.CONFIRM_RESET_PSW :
            return (
                <AuthCard>
                    <Typography level="body-md" textAlign="center" >
                        {t("emailsent")}
                    </Typography>

                    <Button variant="solid"
                            color="primary"
                            onClick={() => setTab(TABS.LOGIN)}
                    >
                        {t("backtologin")}
                    </Button>
                </AuthCard>
            )
    }


    async function SignIn(email: string, password: string, url: { from: string } | null) {
        try {
            await firebase.auth().signInWithEmailAndPassword(email, password)
            if(url) window.location.replace(url.from)
        } catch (e: unknown) {
            setError(e)
        }
    }

    // La riga MySQL (+ Customer Stripe) per un utente Google/Apple non
    // esiste finché POST /registerSocialUser non viene chiamata (Firebase
    // crea l'utente lato client, non passa da POST /register come
    // email/password) — la chiamata di recovery vera e propria è in
    // AuthProvider.tsx (resolveUser), qui basta autenticarsi con Firebase.
    async function SignInWithSocial(kind: "google" | "apple", url: { from: string } | null) {
        setSocialLoading(kind);
        try {
            const provider = kind === "google"
                ? new firebase.auth.GoogleAuthProvider()
                : new firebase.auth.OAuthProvider("apple.com");
            if (kind === "apple") {
                provider.addScope("email");
                provider.addScope("name");
            }
            await firebase.auth().signInWithPopup(provider);
            if (url) window.location.replace(url.from)
        } catch (e: unknown) {
            const code = (e as {code?: string})?.code;
            if (!code || !IGNORED_SOCIAL_ERROR_CODES.has(code)) {
                setError(e)
            }
        } finally {
            setSocialLoading(null);
        }
    }
    async function ResetPsw(email: string) {
        try {
            await firebase.auth().sendPasswordResetEmail(email).then(() => {
                setTab(TABS.CONFIRM_RESET_PSW)
            })
        } catch (e: unknown) {
            setError(e)
        }
    }
}
